import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeftRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

export default function ProductGallery({ products }) {
  const trackRef = useRef(null);
  const [constraint, setConstraint] = useState(0);
  const { addItem } = useCart();

  useEffect(() => {
    const calc = () => {
      if (trackRef.current) {
        setConstraint(
          trackRef.current.scrollWidth - trackRef.current.offsetWidth
        );
      }
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, [products]);

  return (
    <section
      id="gallery"
      data-testid="product-gallery"
      className="relative py-24 bg-black overflow-hidden"
    >
      <div className="px-6 md:px-12 flex items-end justify-between mb-12">
        <div>
          <p className="font-mono-tech text-xs uppercase tracking-[0.3em] text-steel-light mb-3">
            02 — The Line
          </p>
          <h2 className="font-display text-5xl md:text-7xl font-extrabold tracking-tighter text-white">
            Rolling Stock
          </h2>
        </div>
        <div className="hidden md:flex items-center gap-6">
          <Link
            to="/shop"
            data-testid="view-catalogue-link"
            className="font-mono-tech text-[10px] uppercase tracking-widest text-white border-b border-white/40 hover:border-white pb-1 transition-colors duration-300"
          >
            View full catalogue
          </Link>
          <div className="flex items-center gap-2 text-steel-light">
            <ArrowLeftRight className="w-4 h-4" />
            <span className="font-mono-tech text-[10px] uppercase tracking-widest">
              Drag to move through cars
            </span>
          </div>
        </div>
      </div>

      <motion.div ref={trackRef} className="cursor-grab active:cursor-grabbing">
        <motion.div
          className="flex gap-6 px-6 md:px-12"
          drag="x"
          dragConstraints={{ left: -constraint, right: 0 }}
          dragElastic={0.08}
          dragTransition={{ power: 0.3, timeConstant: 200 }}
        >
          {products.map((p, i) => (
            <ProductPanel key={p.id} product={p} index={i} onAdd={addItem} />
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}

function ProductPanel({ product, index, onAdd }) {
  const [size, setSize] = useState(product.sizes[2] || product.sizes[0]);
  const [color, setColor] = useState(product.colors[0]);

  return (
    <motion.div
      data-testid={`gallery-panel-${product.id}`}
      className="relative flex-shrink-0 w-[85vw] md:w-[60vw] lg:w-[46vw] h-[70vh] overflow-hidden border border-white/10 bg-steel-dark/40 select-none"
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay: index * 0.05 }}
    >
      <motion.img
        src={product.image}
        alt={product.name}
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover saturate-[0.5]"
        whileHover={{ scale: 1.04 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />

      <div className="absolute top-6 left-6">
        <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/70 border border-white/20 px-3 py-1">
          {product.category}
        </span>
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
        <div className="flex items-end justify-between mb-5">
          <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-white">
            {product.name}
          </h3>
          <span className="font-mono-tech text-lg text-white">
            ${product.price.toFixed(0)}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-5">
          {product.sizes.map((s) => (
            <button
              key={s}
              data-testid={`size-${product.id}-${s}`}
              onClick={() => setSize(s)}
              className={`font-mono-tech text-[11px] px-3 py-1 border transition-colors duration-200 ${
                size === s
                  ? "bg-white text-black border-white"
                  : "text-white/80 border-white/25 hover:border-white"
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-6">
          {product.colors.map((c) => (
            <button
              key={c}
              data-testid={`color-${product.id}-${c}`}
              onClick={() => setColor(c)}
              className={`font-mono-tech text-[10px] uppercase tracking-wide px-2 py-1 transition-colors duration-200 ${
                color === c ? "text-white" : "text-steel-light hover:text-white"
              }`}
            >
              {color === c ? `[ ${c} ]` : c}
            </button>
          ))}
        </div>

        <button
          data-testid={`add-to-cart-${product.id}`}
          onClick={() => {
            onAdd(product, size, color);
            toast.success(`${product.name} added`, {
              description: `${size} · ${color}`,
            });
          }}
          className="group relative w-full md:w-auto overflow-hidden border border-white bg-white text-black px-8 py-3"
        >
          <span className="relative z-10 font-mono-tech text-xs uppercase tracking-widest group-hover:text-white transition-colors duration-300">
            Add to Cart
          </span>
          <span className="absolute inset-0 bg-black translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
        </button>
      </div>
    </motion.div>
  );
}
